import React from 'react';
import { Link } from 'react-router-dom';
import { IcLogOut, IcSun, IcMoon, IcMessageCircle, IcUser } from '../../theme/icons';

/**
 * Slide-in "More" drawer: opens from the right, full height (lengthwise) and
 * two-thirds of the screen width. User chip, a grid of role-filtered shortcuts,
 * logout. Styled with the same tokens as the desktop pages (white surface,
 * rounded-xl tiles, slate icon chips, blue accents).
 */
export default function MoreSheet({ open, onClose, links, user, onLogout, theme = 'dark', onToggleTheme, notesUnread = 0, onNotes }) {
  const isDark = theme === 'dark';
  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-50 bg-black/40 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      <div
        role="dialog"
        aria-hidden={!open}
        className={`fixed z-50 top-0 right-0 h-full w-2/3 max-w-xs flex flex-col bg-transparent transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Bloc en haut, aligné avec le header (Ahmed 2026-08-16) : carte
            profil + fenêtre de 5 cartes ; le reste (dont Mode clair et
            Déconnexion) se rejoint en scrollant dans la liste. */}
        <div className="flex flex-col h-full px-4 pt-2 pb-4">
          {/* Carte profil FIXE au-dessus de la liste (Ahmed 2026-08-16) :
              seule la liste des pages défile. */}
          <div className="flex items-center gap-3 p-3 mb-2 rounded-xl bg-gray-100 shrink-0">
            {/* Avatar de profil (Ahmed 2026-08-16) : silhouette humaine,
                pas d'initiale — comme l'avatar des clients. */}
            <div className="w-11 h-11 rounded-full bg-gray-100 text-gray-900 flex items-center justify-center shrink-0">
              <IcUser className="text-lg" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-gray-800 truncate">
                {user?.username || 'Utilisateur'}
              </div>
              <div className="text-xs text-gray-500 capitalize">{user?.role || ''}</div>
            </div>
            {/* Notes dans la carte profil, en face de l'avatar (Ahmed
                2026-08-16) — l'ancienne carte Notes séparée est supprimée. */}
            {onNotes && (
              <button
                onClick={onNotes}
                aria-label={notesUnread > 0 ? `Notes — ${notesUnread} non lue(s)` : 'Notes'}
                className="ml-auto relative w-11 h-11 rounded-full bg-gray-100 text-gray-900 flex items-center justify-center shrink-0 active:bg-gray-200 transition-colors"
              >
                <IcMessageCircle className="text-lg" />
                {notesUnread > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-gray-100">
                    {notesUnread > 99 ? '99+' : notesUnread}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* 5 cartes de 64px + 4 gaps de 8px = fenêtre de 352px — la liste
              des pages (avec thème + déconnexion en fin) défile seule. */}
          <div className="flex flex-col gap-2 overflow-y-auto max-h-[352px] -mx-1 px-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {links.map((l, i) => {
              // Section du menu (groupes par fréquence d'usage — Ahmed 2026-08-16).
              if (l.heading) {
                return (
                  <p key={i} className="px-1 pt-2 pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 first:pt-0">
                    {l.heading}
                  </p>
                );
              }
              const Icon = l.icon;
              return (
                <Link
                  key={i}
                  to={l.to}
                  onClick={onClose}
                  className="flex flex-row items-center gap-3 p-3 rounded-xl bg-gray-100 text-gray-700 active:bg-gray-200 transition-colors"
                >
                  <span className="w-10 h-10 rounded-xl bg-white text-gray-900 border border-gray-900 flex items-center justify-center shrink-0">
                    <Icon className="text-lg" />
                  </span>
                  <span className="text-sm leading-tight">{l.label}</span>
                </Link>
              );
            })}

            {/* Mode clair/sombre et Déconnexion défilent avec les cartes,
                en fin de liste (Ahmed 2026-08-16). */}
            <button
              onClick={onToggleTheme}
              className="w-full shrink-0 flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl bg-gray-100 text-gray-700 active:bg-gray-200 transition-colors"
            >
              <span className="flex items-center gap-2 font-medium">
                {isDark ? <IcMoon className="text-blue-600" /> : <IcSun className="text-amber-500" />}
                {isDark ? 'Mode sombre' : 'Mode clair'}
              </span>
              <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDark ? 'bg-blue-600' : 'bg-amber-500'}`}>
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${isDark ? 'translate-x-5' : 'translate-x-1'}`} />
              </span>
            </button>

            <button
              onClick={onLogout}
              className="w-full shrink-0 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gray-100 text-red-600 font-medium hover:bg-gray-200 transition-colors"
            >
              <IcLogOut /> Déconnexion
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
